import { comparer, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as debounce from 'lodash.debounce';
import * as React from 'react';

/* tslint:disable:max-file-line-count */
import { ClickTracker } from 'bernie-analytics';
import { AnalyticsStore, ExperimentStore, merge } from 'bernie-core';
import { ClientLogger } from 'bernie-client';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { FilterHeaderSize } from 'src/common/constants/filter-header-config';
import { SortAndFilterModel } from './fields/filter-types';

import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkHeading } from 'uitk-react-text';

import { onClickScrollToTarget } from 'utils/smooth-scroll';

import { CarPickupDropOffTimeRange } from 'components/multi-item/search-wizard/car-pickup-dropoff-time-range';
import { EssClientLogger } from 'typeahead-client-logger';
import { fieldMap } from './fields/all-fields';
import { Form } from 'components/common/form';
import { FormFieldBindingComponent } from './fields/form-field-binding';

const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.searchform');
const { withoutKeys } = merge;
const ButtonTracked = ClickTracker(UitkPrimaryButton);

export interface SearchFormProps {
  analytics?: AnalyticsStore;
  model: SearchCriteriaModel;
  formAction?: string;
  displayFields?: string[];
  ignoreFields?: string[];
  fieldNameOverrides?: { [key: string]: string }; // modelKey => fieldName
  sortAndFilterOptions?: SortAndFilterModel;
  sortAndFilterDefaults?: any;
  callToAction?: string;
  routeStore?: any;
  useProgressIndicator?: boolean;
  uiPage?: any;
  onUpdateResults?: any;
  replace?: boolean;
  afterSubmit?: any;
  renderSearchFormTopSection?: any;
  renderSearchFormBottomSection?: any;
  fieldsWrapper?: any;
  className?: string;
  provideDefaultSubmitButton?: boolean;
  wrapperClasses?: object;
  autoSubmit?: boolean;
  showErrorValidationOnSubmit?: boolean;
  formRef?: (any) => void;
  fetchDatePickerRates?: () => Promise<object>;
  searchable?: boolean;
  currentHotel?: CurrentHotelStore;
  shouldShowMultiItemWizardFields?: boolean;
  isMobile?: boolean;
  experiment?: ExperimentStore;
}

export interface SearchFormState {
  model: SearchCriteriaModel;
  submitDisabled: boolean;
  isSubmitted: boolean;
  mounted: boolean;
}

export interface SearchFormFieldProps extends FormFieldBindingComponent {
  model: SearchCriteriaModel;
  modelKey: string;
  sortAndFilterOptions?: SortAndFilterModel;
  validators: object[];
  updateModelCallback?: (event) => void;
  wrapperClassNames?: string;
  fieldNameOverrides?: { [key: string]: string }; // modelKey => fieldName
  autoSubmit?: boolean;
  analytics?: AnalyticsStore;
  isSubmitted?: boolean;
  showErrorValidationOnSubmit?: boolean;
  fetchDatePickerRates?: () => Promise<object>;
  essAnalyticsLogger?: EssClientLogger;
  showSeparator?: boolean;
  callToAction?: string;
  rfrrSuffix?: () => void;
}

// NOTE: BFF may provide the keys in the future
export const alwaysIgnoredKeys = ['changedFilters', 'logger'];

@withLocalization
@inject('analytics', 'routeStore', 'uiPage', 'currentHotel', 'experiment')
@observer
export class SearchForm extends React.Component<LocalizedComponentProps & SearchFormProps, SearchFormState> {
  constructor(props) {
    super(props);

    const model = props.model ? props.model.clone() : new SearchCriteriaModel();

    this.state = {
      model,
      submitDisabled: false,
      isSubmitted: false,
      mounted: false
    };

    this.handleAfterSubmit = this.handleAfterSubmit.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);

    this.getFormRef = this.getFormRef.bind(this);
    this.handleAutoSubmit = this.handleAutoSubmit;
    this.dispatchSubmitEvent = debounce(this.dispatchSubmitEvent.bind(this), 200);
    this.rfrrSuffix = this.rfrrSuffix.bind(this);

    this.essAnalyticsLogger = new EssClientLogger(props.analytics, ClientLogger, (model || {}).destination);
  }

  private validators = [];
  private formRef?: Form;
  private essAnalyticsLogger: EssClientLogger;

  public static defaultProps = {
    displayFields: [],
    ignoreFields: [],
    fieldNameOverrides: {},
    provideDefaultSubmitButton: true,
    autoSubmit: false,
    l10n: {}
  };

  public componentWillReceiveProps(nextProps) {
    if (nextProps.model !== this.props.model) {
      this.setState({
        model: nextProps.model ? nextProps.model.clone() : new SearchCriteriaModel()
      });
    }
  }

  public componentDidMount() {
    if (this.props.formRef) {
      this.props.formRef(this);
    }

    this.setState({
      mounted: true
    });
  }

  public componentWillUnmount() {
    if (this.props.formRef) {
      this.props.formRef(null);
    }
  }

  private get hiddenFields() {
    const { displayFields, ignoreFields } = this.props;
    const { model, mounted } = this.state;
    const excludedKeys = [];

    if (!mounted) {
      excludedKeys.push('latLong', 'regionId');
    }

    return Object.keys(withoutKeys([...alwaysIgnoredKeys, ...ignoreFields, ...displayFields, ...excludedKeys]).merge({}, model));
  }

  public get modelDefaults() {
    const modelDefaults = SearchCriteriaModel && SearchCriteriaModel.DEFAULTS;
    const sortAndFilterDefaults = this.props.sortAndFilterDefaults;

    // merging defaults from bff response
    if (sortAndFilterDefaults) {
      Object.assign(modelDefaults, sortAndFilterDefaults);
    }

    return modelDefaults || {};
  }

  @computed
  public get displayFieldsActive() {
    const { model } = this.state;
    const { displayFields, fieldNameOverrides } = this.props;

    const displayFieldsActive = displayFields.reduce((activeFields, fieldKey) => {
      // field name override support added to use existing model keys with a new displayField
      const key = fieldNameOverrides[fieldKey] || fieldKey;
      const value = model && model[key];
      const defaultValue = this.modelDefaults[key];

      // if the value and defaultValue are not the same, push fieldKey to activeFields
      if (!comparer.structural(value, defaultValue)) {
        activeFields.push(key);
      }

      return activeFields;
    }, []);

    return displayFieldsActive;
  }

  @computed
  public get displayFieldsActiveLength() {
    return this.displayFieldsActive && this.displayFieldsActive.length;
  }

  public clearDisplayfields() {
    const { model } = this.state;

    // reset all active display fields
    this.displayFieldsActive.forEach(fieldKey => {
      const defaultValue = this.modelDefaults[fieldKey];

      if (model && fieldKey) {
        model[fieldKey] = defaultValue;
      }
    });
  }

  private rfrrSuffix(optionValue): string {
    const { experiment } = this.props;
    if (!experiment || !experiment.exposures) {
      return '';
    }
    if (
      optionValue === 'BEST_DEAL' &&
      experiment.exposures.PWA_Deals_Relevant_Sort &&
      experiment.exposures.PWA_Deals_Relevant_Sort.bucket === 1
    ) {
      return '.RelevantDeal';
    }

    return '';
  }

  private handleChange(event?: Event) {
    if (document && document.dispatchEvent && typeof document.dispatchEvent === 'function' && CustomEvent) {
      document.dispatchEvent(
        new CustomEvent('mesoSlotFilterUpdate', {
          detail: { type: 'slotFilter', editType: 'adding', filterData: event.target }
        })
      );
    }
    if (this.props.autoSubmit) {
      this.handleAutoSubmit(event);
    } else {
      // enable submit button after model change
      this.setState({ submitDisabled: false });
    }
  }
  private dispatchSubmitEvent(): void {
    const event: Event = new Event('submit', { cancelable: true });
    this.formRef.formElement.dispatchEvent(event);
  }
  private handleAutoSubmit(event?) {
    const { model } = this.state;
    if (document && this.formRef && this.formRef.formElement) {
      // Force the component to rerender with the updated model before submitting
      // This ensures the changes to the form values take hold before being submitted.
      this.setState(
        {
          model
        },
        () => {
          this.dispatchSubmitEvent();
        }
      );
    }
  }

  private handleSubmit(event?: Event) {
    const { onUpdateResults, uiPage, useProgressIndicator, shouldShowMultiItemWizardFields, isMobile } = this.props;
    if (this.essAnalyticsLogger) {
      this.essAnalyticsLogger.logWizardSubmitEvent();
    }
    this.setState({ submitDisabled: true, isSubmitted: true });
    const errors = [];
    this.validators.forEach(validator => {
      const error = validator();
      if (error) {
        errors.push(error);
      }
    });

    if (errors.length > 0) {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      const firstFocusableError = errors.find(error => error.focus);
      if (firstFocusableError) {
        firstFocusableError.focus();
      }

      this.setState({ submitDisabled: false });

      if (logger && typeof logger.logEvent === 'function') {
        logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM_HANDLE_SUBMIT_ERROR'), errors.join('|'));
      }
    } else {
      if (shouldShowMultiItemWizardFields && isMobile) {
        onClickScrollToTarget({ ref: this.formRef.formElement });
      }

      if (useProgressIndicator) {
        uiPage.showProgressIndicator();
      }

      this.props.analytics.resetAnalyticsForNewPage();

      if (typeof onUpdateResults === 'function') {
        onUpdateResults(event, this.state.model);
      }
    }
  }

  private handleAfterSubmit({ action, formData, queryString, submitUrl }) {
    const { model } = this.state;
    const { afterSubmit } = this.props;

    if (typeof afterSubmit === 'function') {
      afterSubmit({ action, formData, queryString, submitUrl, model });
    }
  }

  private get renderMobileMultiItemWizardTopSection() {
    return (
      this.props.shouldShowMultiItemWizardFields && (
        <Viewport>
          <ViewSmall>
            <div className="uitk-cell all-y-gutter-three all-b-padding-one">
              <UitkHeading level={4} size={400}>
                {this.props.l10n.formatText('multiItem.searchWizard.mobile.hotelLabel')}
              </UitkHeading>
            </div>
          </ViewSmall>
          <ViewMedium />
        </Viewport>
      )
    );
  }

  private get renderMultiItemWizardCarFields() {
    return (
      this.props.shouldShowMultiItemWizardFields && (
        <div className="uitk-cell s-cell-1-1 m-cell-2-3 l-cell-1-2 xl-cell-1-2">
          <CarPickupDropOffTimeRange
            model={this.state.model}
            modelKey="multiItemCarTimeRange"
            updateModelCallback={this.handleChange}
            validators={this.validators}
          />
        </div>
      )
    );
  }

  public render() {
    const {
      displayFields,
      renderSearchFormTopSection,
      renderSearchFormBottomSection,
      fieldsWrapper,
      className,
      provideDefaultSubmitButton,
      callToAction,
      fieldNameOverrides,
      wrapperClasses,
      autoSubmit,
      analytics,
      fetchDatePickerRates,
      currentHotel,
      sortAndFilterOptions
    } = this.props;
    const { model, mounted } = this.state;
    const formAction = this.props.formAction || '/Hotel-Search';
    const FieldsWrapperTag = fieldsWrapper ? fieldsWrapper.Tag : React.Fragment;
    const fieldsWrapperProps = fieldsWrapper ? { className: fieldsWrapper.className } : {};

    if (typeof window === 'undefined' && currentHotel) {
      Object.assign(model, currentHotel.hsrQueryValues);
    }

    return (
      <Form
        className={className}
        method="GET"
        onSubmit={this.handleSubmit}
        afterSubmit={this.handleAfterSubmit}
        action={formAction}
        replace={this.props.replace}
        noValidate={true}
        wrappedComponentRef={this.getFormRef}
        routeStore={this.props.routeStore}
      >
        {this.renderMobileMultiItemWizardTopSection}
        {renderSearchFormTopSection && renderSearchFormTopSection(this.state)}
        <FieldsWrapperTag {...fieldsWrapperProps}>
          {displayFields &&
            displayFields.length > 0 &&
            displayFields.map(field => (
              <DisplayField
                key={`displayField-${field}`}
                field={field}
                model={model}
                validators={this.validators}
                analytics={analytics}
                handleChange={this.handleChange}
                sortAndFilterOptions={sortAndFilterOptions}
                wrapperClasses={wrapperClasses}
                fieldNameOverrides={fieldNameOverrides}
                autoSubmit={autoSubmit}
                isSubmitted={this.state.isSubmitted}
                showErrorValidationOnSubmit={this.props.showErrorValidationOnSubmit}
                fetchDatePickerRates={fetchDatePickerRates}
                essAnalyticsLogger={this.essAnalyticsLogger}
                callToAction={callToAction}
                customProps={{
                  headerSize: (field === 'sort' && FilterHeaderSize.HEADING_500) || FilterHeaderSize.HEADING_300
                }}
                rfrrSuffix={this.rfrrSuffix}
              />
            ))}
          {this.hiddenFields &&
            this.hiddenFields.length > 0 &&
            this.hiddenFields.map(field => (
              <HiddenField
                key={`hidden_${field}`}
                field={field}
                fieldNameOverrides={fieldNameOverrides}
                sortAndFilterOptions={sortAndFilterOptions}
                wrapperClasses={wrapperClasses}
                model={model}
                validators={this.validators}
                handleChange={this.handleChange}
                analytics={analytics}
                rfrrSuffix={this.rfrrSuffix}
              />
            ))}
        </FieldsWrapperTag>
        {renderSearchFormBottomSection && renderSearchFormBottomSection(this.state)}
        {provideDefaultSubmitButton && (!model.isDateless || !mounted) && (
          <DefaultSubmitButton callToAction={callToAction} submitDisabled={this.state.submitDisabled} autoSubmit={this.props.autoSubmit} />
        )}
        {this.renderMultiItemWizardCarFields}
      </Form>
    );
  }
  private getFormRef(ref) {
    this.formRef = ref;
  }
}
export const DefaultSubmitButton = ({ callToAction, submitDisabled, autoSubmit }) => {
  const submitButtonProps = {
    type: 'submit',
    rfrr: 'FORM.SUBMIT',
    linkName: 'Submit search form',
    isFullWidth: true,
    size: UitkButtonSize.LARGE,
    'data-stid': 'submit-hotel-search'
  };

  const [submitButtonMounted, setSubmitButtonMounted] = React.useState(false);
  React.useEffect(() => {
    setSubmitButtonMounted(true);
  }, []);

  const buttonLabel = callToAction || <LocalizedText message="hotels.search.form.submit.label" />;

  return (
    <Viewport>
      <ViewSmall>
        {(!autoSubmit || !submitButtonMounted) && (
          <div className="uitk-cell all-cell-fill">
            <ButtonTracked {...submitButtonProps} disabled={submitDisabled}>
              {buttonLabel}
            </ButtonTracked>
          </div>
        )}
      </ViewSmall>
      <ViewMedium>
        <div className="uitk-cell all-cell-fill">
          <ButtonTracked {...submitButtonProps}>{buttonLabel}</ButtonTracked>
        </div>
      </ViewMedium>
    </Viewport>
  );
};
DefaultSubmitButton['displayName'] = 'DefaultSubmitButton';
export const HiddenField = ({
  field,
  fieldNameOverrides,
  model,
  validators,
  handleChange,
  sortAndFilterOptions,
  wrapperClasses,
  analytics,
  rfrrSuffix
}) => {
  /*
    Fields that need processing before it can be added to the form to be submitted
    e.g: rooms:Array<RoomModel> --> adults: string, children: string
    This processing should be delegated to the component itself
   */
  const fieldKeysNeedMutation = ['rooms'];
  if (fieldKeysNeedMutation.includes(field)) {
    return (
      <DisplayField
        key={`displayField-${field}`}
        field={field}
        model={model}
        customProps={{ hidden: true }}
        validators={validators}
        handleChange={handleChange}
        sortAndFilterOptions={sortAndFilterOptions}
        wrapperClasses={wrapperClasses}
        fieldNameOverrides={fieldNameOverrides}
        autoSubmit={false}
        analytics={analytics}
        isSubmitted={false}
        showErrorValidationOnSubmit={false}
        rfrrSuffix={rfrrSuffix}
      />
    );
  }
  const attributes = {
    type: 'hidden',
    name: fieldNameOverrides[field] || field,
    value: model[field] || ''
  };
  if (attributes.value === '' || (typeof attributes.value.length === 'number' && attributes.value.length === 0)) {
    attributes['disabled'] = true;
  }

  return <input {...attributes} />;
};
HiddenField['displayName'] = 'HiddenField';
export const DisplayField = ({
  field,
  model,
  sortAndFilterOptions,
  wrapperClasses,
  fieldNameOverrides,
  validators,
  handleChange,
  autoSubmit,
  analytics,
  isSubmitted,
  showErrorValidationOnSubmit,
  customProps = {},
  fetchDatePickerRates = null,
  essAnalyticsLogger = null,
  showSeparator = true,
  callToAction = null,
  rfrrSuffix
}) => {
  const SearchFormField: React.ComponentClass<SearchFormFieldProps> = fieldMap[field];
  if (!SearchFormField) {
    return null;
  }

  return (
    <SearchFormField
      model={model}
      modelKey={field}
      sortAndFilterOptions={sortAndFilterOptions}
      validators={validators}
      fieldNameOverrides={fieldNameOverrides}
      updateModelCallback={handleChange}
      wrapperClassNames={wrapperClasses && wrapperClasses[field]}
      autoSubmit={autoSubmit}
      analytics={analytics}
      isSubmitted={isSubmitted}
      showErrorValidationOnSubmit={showErrorValidationOnSubmit}
      fetchDatePickerRates={fetchDatePickerRates}
      essAnalyticsLogger={essAnalyticsLogger}
      showSeparator={showSeparator}
      callToAction={callToAction}
      rfrrSuffix={rfrrSuffix}
      {...customProps}
    />
  );
};
DisplayField['displayName'] = 'DisplayField';
